/**
 * Client-side auth state for the SPA.
 *
 * The SDK hooks (`useTensileAuth`, `useAppVault`) are the storage primitives, but the pages need
 * one shared instance: `useTensileAuth` keeps its state per hook call and only syncs across tabs,
 * so two independent calls would disagree after sign-in in the same tab. This provider owns the
 * single instance and adds the server round-trips + the app-vault unlock.
 *
 * The Master Identity seed lives in memory only. The derived app-scoped key is stored in
 * sessionStorage by `useAppVault` so a reload in the same tab keeps the vault usable.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  assertPasskeyWithPrf,
  bytesToBase64,
  computeIdentityFingerprint,
  exportAppScopedSecretKeyB64,
  generateMasterIdentitySeed,
  registerPasskeyWithPrf,
  unwrapMasterKeyFromDevice,
  wrapMasterKeyForDevice,
  type Bytes,
  type TensileAuthSession,
} from "@tensilelabs/auth-client";
import { useAppVault, useTensileAuth } from "@tensilelabs/auth-client/react";
import {
  fetchAccount,
  passkeyLogin,
  registerPasskey,
  requestOtp,
  verifyOtp,
  type AccountSnapshot,
  type OtpTicket,
} from "../api";

export const APP_ID = "taskjinn";

interface AuthContextValue {
  session: TensileAuthSession | null;
  account: AccountSnapshot | null;
  identityFingerprint: string | null;
  vaultUnlocked: boolean;
  appScopedSecretKeyB64: string | null;
  busy: boolean;
  error: string | null;
  isAdmin: boolean;
  clearError: () => void;
  requestOtp: (email: string) => Promise<{ ticket: OtpTicket; devOtpPreview?: string }>;
  completeSignup: (args: {
    email: string;
    ticket: OtpTicket;
    code: string;
    deviceName: string;
  }) => Promise<void>;
  signIn: (email: string) => Promise<void>;
  unlockVault: () => Promise<void>;
  signOut: () => void;
  refreshAccount: () => Promise<AccountSnapshot | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useTensileAuth();
  const vault = useAppVault(APP_ID);
  const { unlock: unlockVaultKey, lock: lockVaultKey } = vault;
  const [identityFingerprint, setIdentityFingerprint] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const session = useMemo<TensileAuthSession | null>(() => {
    if (!auth.token) return null;
    return {
      token: auth.token,
      email: auth.email,
      accountId: auth.accountId,
      role: auth.role,
    };
  }, [auth.token, auth.email, auth.accountId, auth.role]);

  const unlockWithSeed = useCallback(
    async (seed: Bytes) => {
      const appKey = await exportAppScopedSecretKeyB64(seed, APP_ID);
      unlockVaultKey(appKey);
      setIdentityFingerprint(await computeIdentityFingerprint(seed));
    },
    [unlockVaultKey],
  );

  const refreshAccount = useCallback(async () => {
    if (!auth.token) return null;
    try {
      const snapshot = await fetchAccount();
      setAccount(snapshot);
      return snapshot;
    } catch (cause) {
      // A stale token must not leave the UI claiming to be signed in.
      if (cause instanceof Error && /unauthorized|401/i.test(cause.message)) {
        auth.signOut();
        setAccount(null);
      }
      return null;
    }
  }, [auth]);

  // Load the account on mount and whenever the token changes (sign-in, unlock, other tab).
  useEffect(() => {
    if (auth.token) void refreshAccount();
    else setAccount(null);
  }, [auth.token, refreshAccount]);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    setBusy(true);
    setError(null);
    try {
      return await action();
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : "Something went wrong";
      setError(message);
      throw cause;
    } finally {
      setBusy(false);
    }
  }, []);

  const completeSignup = useCallback<AuthContextValue["completeSignup"]>(
    (args) =>
      run(async () => {
        const email = args.email.trim().toLowerCase();
        const verified = await verifyOtp({ email, code: args.code, ticket: args.ticket });
        const ceremony = await registerPasskeyWithPrf({
          email,
          userId: email,
          challengeB64: verified.challenge_b64,
          rpId: verified.webauthn_rp_id,
        });
        const seed = generateMasterIdentitySeed();
        const deviceEnvelope = await wrapMasterKeyForDevice({
          masterSeed: seed,
          deviceId: `dev-${crypto.randomUUID().slice(0, 8)}`,
          deviceName: args.deviceName,
          credentialIdB64: ceremony.credentialIdB64,
          passkeyPrfOutput: ceremony.prfOutput,
          prfSalt: ceremony.prfSalt,
        });
        const registered = await registerPasskey({
          email,
          displayName: email.split("@")[0],
          deviceEnvelope,
        });
        auth.setSession({
          token: registered.token,
          email,
          accountId: registered.account_id,
          role: registered.role,
        });
        await unlockWithSeed(seed);
        await refreshAccount();
      }),
    [auth, refreshAccount, run, unlockWithSeed],
  );

  const signIn = useCallback<AuthContextValue["signIn"]>(
    (emailInput) =>
      run(async () => {
        const email = emailInput.trim().toLowerCase();
        const login = await passkeyLogin({ email });
        const envelope = login.device_envelope;
        if (!envelope) {
          throw new Error("No authorized passkey device was found for this account");
        }
        const ceremony = await assertPasskeyWithPrf({
          email,
          challengeB64: bytesToBase64(crypto.getRandomValues(new Uint8Array(32))),
          prfSaltB64: envelope.prf_salt_b64,
          credentialIdB64: envelope.credential_id_b64,
          rpId: "tensilelabs.com",
        });
        const seed = await unwrapMasterKeyFromDevice(envelope, ceremony.prfOutput);
        auth.setSession({
          token: login.token,
          email,
          accountId: login.account_id,
          role: login.role,
        });
        await unlockWithSeed(seed);
        await refreshAccount();
      }),
    [auth, refreshAccount, run, unlockWithSeed],
  );

  const unlockVault = useCallback<AuthContextValue["unlockVault"]>(
    () =>
      run(async () => {
        const email = auth.email;
        if (!email) throw new Error("Sign in before unlocking the vault");
        const login = await passkeyLogin({ email });
        const envelope = login.device_envelope;
        if (!envelope) throw new Error("No authorized passkey device was found");
        const ceremony = await assertPasskeyWithPrf({
          email,
          challengeB64: bytesToBase64(crypto.getRandomValues(new Uint8Array(32))),
          prfSaltB64: envelope.prf_salt_b64,
          credentialIdB64: envelope.credential_id_b64,
          rpId: "tensilelabs.com",
        });
        const seed = await unwrapMasterKeyFromDevice(envelope, ceremony.prfOutput);
        auth.setSession({
          token: login.token,
          email,
          accountId: login.account_id,
          role: login.role,
        });
        await unlockWithSeed(seed);
        await refreshAccount();
      }),
    [auth, refreshAccount, run, unlockWithSeed],
  );

  const signOut = useCallback(() => {
    auth.signOut();
    lockVaultKey();
    setIdentityFingerprint(null);
    setAccount(null);
    setError(null);
  }, [auth, lockVaultKey]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      account,
      identityFingerprint,
      vaultUnlocked: vault.unlocked,
      appScopedSecretKeyB64: vault.appScopedSecretKeyB64,
      busy,
      error,
      isAdmin: session?.role === "owner" || session?.role === "admin",
      clearError: () => setError(null),
      requestOtp: async (email: string) => {
        const response = await requestOtp(email.trim().toLowerCase());
        return { ticket: response.ticket, devOtpPreview: response.dev_otp_preview };
      },
      completeSignup,
      signIn,
      unlockVault,
      signOut,
      refreshAccount,
    }),
    [
      session,
      account,
      identityFingerprint,
      vault.unlocked,
      vault.appScopedSecretKeyB64,
      busy,
      error,
      completeSignup,
      signIn,
      unlockVault,
      signOut,
      refreshAccount,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}
